import { IDocument } from '../cloud/database/DatabaseItemsDefinitions'
import { IApprovalRequest } from './Approval'
import { IContract } from './Contract'
import { IExecutionEnvironment } from './NodesExecution'
import { NodeDataMap, NodeDryRunSettingsMap, NodeInputMap, NodeOutputMap } from './NodeTypes'
import { IVersioning } from './Versioning'

export type FlowType = 'flow' | 'sub-flow'

export type GroupBox = {
    uuid?: string
    coords?: { x: number, y: number }
    width?: number
    height?: number
}

export interface JsonSchema {
    $schema?: string
    title?: string
    type?: 'string' | 'number' | 'boolean' | 'null' | 'array' | 'object'
    format?: 'date-time'
    items?: JsonSchema
    properties?: { [key: string]: JsonSchema }
}

export type DataType = 'string' | 'number' | 'boolean' | 'date' | 'object' | 'array'

export type ComparisonNumber = ['exists' | 'does not exist' | 'is empty' | 'is not empty' | 'is equal to' | 'is not equal to' | 'is greater than' | 'is less than' | 'is greater than or equal to' | 'is less than or equal to']
export type ComparisonString = ['exists' | 'does not exist' | 'is empty' | 'is not empty' | 'is equal to' | 'is not equal to' | 'contains' | 'does not contain' | 'starts with' | 'does not start with' | 'ends with' | 'does not end with']
export type ComparisonDate = ['exists' | 'does not exist' | 'is empty' | 'is not empty' | 'is equal to' | 'is not equal to' | 'is after' | 'is before' | 'is after or equal to' | 'is before or equal to']
export type ComparisonBoolean = ['exists' | 'does not exist' | 'is empty' | 'is not empty' | 'is true' | 'is false' | 'is equal to' | 'is not equal to']
export type AggregationType = 'sum' | 'count' | 'avg' | 'min' | 'max'
export const aggregationOps: AggregationType[] = ['count', 'sum', 'avg', 'min', 'max']

export interface IFlow extends IDocument {
    uuid: string
    name: string
    createdAt: string
    lastModified: string
    versioning: IVersioning
    nodes: INode[]
    globals: { [key in string]: any }
    type?: FlowType
    contractNumbers?: number
}

/**
 * IFlow without nodes
 */
export interface ISimpleFlow extends IDocument {
    uuid: string
    name: string
    createdAt: string
    lastModified: string
    versioning: IVersioning
    type: FlowType
    globals: { [key in string]: any }
}

export interface IFlowResponse {
    uuid: string
    id: number
    planType: 'MAPPO' | 'BCNA'
    externalId: string
    name: string
    engineName?: string
    engineVersion?: number
    createdAt: string
    settlementEngine: IContract['settlementRef']
}

export interface IAncestorFlow {
    ancestorKey: string
    ancestorName: string
    children: {
        uuid: string
        name: string
        version: number
        isHead: boolean
        globals: {
            [key: string]: any
        }
    }[]
}

export interface ISubFlowIO {
    subFlow: IFlow
    inputs: { name: string, type: string }[]
    outputs: { name: string, type: string }[]
}

export interface INodeVariableMode {
    mode: 'custom' | 'copy' | 'merge'
    customData?: Record<string, any>
    copy?: {
        uuid: string
        key: string
    }
}

export interface INodeForkData {
    uuid: string
    inputType: DataType
    keyField: string
    comparison: string
    value: any
    or?: INodeForkData[]
    and?: INodeForkData[]
}

export interface INodeMergeData {
    mergeType: 'combined' | 'join'
    joinType: 'inner' | 'left' | 'right' | 'full' | 'cross'
    keys: { leftKeys: string[], rightKeys: string[] }
    outputColumns?: { leftCols: string[], rightCols: string[] }
}

export type ExpressionConfig = {
    expression: string
    output: string
}

export interface INodeOperationsData {
    expressions: ExpressionConfig[]
}

export interface INodeReportPlaceholderColumn {
    /**
     * The name of the column
     */
    name: string
    /**
     * The path to substitute to the column (id/name/surname...)
     */
    value?: string
}

export interface INodeReportPlaceholder {
    /**
     * Name of text placeholder or dataset
     */
    name: string
    /**
     * The path to substitute the placeholder
     */
    referenceData?: string
    /**
     * The value after the node execution
     */
    value?: any | any[]
    /**
     * If placeholder is a dataset, its associated columns
     */
    columns?: INodeReportPlaceholderColumn[]
    condition?: FilterCondition
}

export interface INodeReport {
    reportUUID: string
    reportName?: string
    placeholders: INodeReportPlaceholder[]
}
export interface AggregationConfig {
    field: string
    op: AggregationType
    as?: string
}

export interface INodeGroupingData {
    groupKeys: string[]
    aggregations: AggregationConfig[]
}

export interface FilterCondition {
    key: string
    operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'includes'
    value: any
}

export interface IFilterData {
    conditions?: FilterCondition[]
    pick?: { type: 'first' | 'last' | 'index' | 'all', value?: number }
    logicalOperator?: 'AND' | 'OR'
}

export interface ICaseRule {
    when: string
    then: string
}

export interface ICaseOptions {
    cases: ICaseRule[]
    else?: string
    output?: string
}

export interface IContractNodeData {
    schemaUUID: string
    properties: { id: string, key: string, outputName: string }[]
}

/**
 * All the keys, that a node of a specific types accepts
 */
export type ArgumentKeyType<T extends NodeType> = keyof NodeInputMap[T]

export type OutputKeyType<T extends NodeType> = keyof NodeOutputMap[T]

/**
 * All the keys, that any node can have in it's output
 */
export type AllOutputKeyType = {
    [K in keyof NodeOutputMap]: NodeOutputMap[K] extends object ? keyof NodeOutputMap[K] : never
}[keyof NodeOutputMap]

export interface INodeInput<T extends NodeType = NodeType> {
    /**
     * The node that is connected to this node and is of input
     */
    uuid: string
    /**
     * The key of the output of the input node. This is needed since nodes can have multiple outputs
     */
    outputKey: AllOutputKeyType
    /**
     * The key of the argument for this node, that the input node output is used for
     */
    argumentKey?: ArgumentKeyType<T>
}

export type NodeType =
    'start' | 'end' | 'fork' | 'end_fork' |
    'ds_dynamo' | 'ds_redshift' | 'ds_contract' |
    'xfrm_sql' | 'xfrm_transform' | 'xfrm_code' | 'xfrm_filterby' | 'xfrm_orderby' | 'xfrm_groupby' | 'xfrm_reduce' | 'xfrm_dataframeops' | 'xfrm_merge' | 'xfrm_operations' | 'xfrm_aggregateBy' | 'xfrm_pivot' | 'xfrm_caseWhen' |
    'var_variable' | 'var_dataframe' |
    'rtg_if' |
    'exe_subFlow' |
    'subFlow_start' | 'subFlow_end' |
    'out_json' | 'out_pdf' | 'out_csv' | 'out_api' | 'out_adis' | 'out_report' |
    'stmt_settlement' |
    'val_human' |
    'dummy' | 'dummy2'

export interface IBaseNode {
    uuid: string
    name: string
    coords: { x: number, y: number }
}

export interface INode<T extends NodeType = NodeType> extends IBaseNode {
    type: T
    inputs: INodeInput<T>[]
    data: NodeDataMap[T]
    dryRunConfig?: NodeDryRunSettingsMap[T]
    /**
     * this is needed for create the box around the fork's branches
     */
    group?: GroupBox
    jsonSchema?: JsonSchema
    overwriteShape?: boolean
    description?: string
}

export type NodeInputValue<T extends NodeType> = NodeInputMap[T] & {
    _fromNode: string
    _fromNodeType: NodeType
    _fromNodeName: string
    _outputKey: AllOutputKeyType
    _inputKey: keyof NodeInputMap[T]
    _schema?: JsonSchema
}

export interface CopyFromFlowItem {
    contractId: number
    contractName: string
    flowId: string
    nodesCount: number
    createdAt: string
    businessKey: string
    productType: string
}

export interface FlowDeletionResponse {
    isDeleted: boolean
    engine: IFlow
}

export interface IFlowWithApproval extends ISimpleFlow {
    approval: {
        uuid: string
        approvalEntityUUID: string
        status: IApprovalRequest['status']
    }
}

// For execution
export interface INodeInputContext<T extends NodeType> {
    /**
     * The node that is receiving this input, so the node that is executing
     */
    node: INode<T>
    /**
     * These are parameters that are global across this entire flow and are shared by all the nodes
     * e.g. The contract for which this settlement flow was executed
     */
    globals: { [key in string]: any }
    /**
     * The arguments for this node, are the outputs from the input nodes
     */
    args: INodeOutputContext<NodeType>[]
    environment: IExecutionEnvironment
}
export interface INodeOutputContext<T extends NodeType> {
    /**
     * The node that produced this output
     */
    node: INode<T>
    output: NodeOutputMap[T]
    /**
     * The resulting shape of this node. Used in the UI to help the user create the settlement engine.
     */
    metadata?: JsonSchema
}

export abstract class BaseNodeEngine<T extends NodeType> {
    abstract execute: (ctx: INodeInputContext<T>) => Promise<INodeOutputContext<T>>
}
